<template>
  <div class="login-page">
    <!-- 语言切换 -->
    <div class="lang-switch">
      <span :class="{ active: currentLang === 'id' }" @click="switchLang('id')">ID</span>
      <span class="lang-divider">|</span>
      <span :class="{ active: currentLang === 'zh' }" @click="switchLang('zh')">中文</span>
    </div>

    <!-- 钉钉SSO加载中 -->
    <div class="login-container" v-if="ssoLoading">
      <div class="loading-box">
        <i class="el-icon-loading" style="font-size: 40px; color: #1677ff;"></i>
        <p style="margin-top: 20px; color: #666; font-size: 15px;">{{ $t('login.dingtalkLoading') }}</p>
      </div>
    </div>

    <!-- 运维人员登录表单 -->
    <div class="login-container" v-else-if="showAdminLogin">
      <div class="login-header">
        <div class="logo-icon">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="#1677ff">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        </div>
        <h1 class="login-title">{{ $t('login.title') }}</h1>
        <p class="login-subtitle">{{ adminUserName }}，{{ $t('login.adminLoginHint') }}</p>
      </div>
      <div class="login-form">
        <p style="font-size:13px;color:#999;margin-bottom:12px;text-align:center;">
          {{ $t('login.adminMobileLabel') }}：{{ adminMobile }}
        </p>
        <div class="input-group">
          <svg class="input-icon" viewBox="0 0 24 24" width="20" height="20" fill="#999">
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/>
          </svg>
          <input v-model="adminPassword" type="password" :placeholder="$t('login.passwordPlaceholder')" class="form-input" @keyup.enter="doAdminLogin" />
        </div>
        <button class="login-btn" @click="doAdminLogin" :disabled="adminLoading">
          {{ adminLoading ? $t('login.loggingIn') : $t('login.loginBtn') }}
        </button>
        <div class="error-msg" v-if="errorMsg"><span>{{ errorMsg }}</span></div>
      </div>
      <div class="login-footer"><span>{{ $t('login.footer') }}</span></div>
    </div>

    <!-- 浏览器直接访问的登录表单（也作为钉钉SSO失败的兜底） -->
    <div class="login-container" v-else>
      <div class="login-header">
        <div class="logo-icon">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="#1677ff">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        </div>
        <h1 class="login-title">{{ $t('login.title') }}</h1>
        <p class="login-subtitle">{{ $t('login.subtitle') }}</p>
      </div>
      <div class="login-form">
        <div class="input-group">
          <svg class="input-icon" viewBox="0 0 24 24" width="20" height="20" fill="#999">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
          <input v-model="userName" type="text" :placeholder="$t('login.usernamePlaceholder')" class="form-input" @keyup.enter="doLogin" />
        </div>
        <div class="input-group">
          <svg class="input-icon" viewBox="0 0 24 24" width="20" height="20" fill="#999">
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/>
          </svg>
          <input v-model="password" type="password" :placeholder="$t('login.passwordPlaceholder')" class="form-input" @keyup.enter="doLogin" />
        </div>
        <button class="login-btn" @click="doLogin" :disabled="loading">
          {{ loading ? $t('login.loggingIn') : $t('login.loginBtn') }}
        </button>
        <div class="error-msg" v-if="errorMsg"><span>{{ errorMsg }}</span></div>
      </div>
      <div class="login-footer"><span>{{ $t('login.footer') }}</span></div>
    </div>
  </div>
</template>

<script>
import { isInDingTalk, getAuthCode } from '../utils/dingtalk'
import { ssoLogin, adminLogin, loginByPassword } from '../api/auth'
import { t, getLang, setLang } from '../utils/i18n'
import { logSSO, logAccess, logError } from '../utils/serverLog'
import { DINGTALK_CORP_ID } from '../config/api'

export default {
  name: 'Login',
  data () {
    return {
      userName: '',
      password: '',
      loading: false,
      ssoLoading: false,
      showAdminLogin: false,
      adminUserName: '',
      adminMobile: '',
      ssoToken: '',
      adminPassword: '',
      adminLoading: false,
      errorMsg: '',
      currentLang: getLang()
    }
  },
  methods: {
    $t (key) { return t(key) },
    switchLang (lang) {
      this.currentLang = lang
      setLang(lang)
      this.$forceUpdate()
    },

    async doDingTalkSSO () {
      this.ssoLoading = true
      this.errorMsg = ''
      logAccess('Login页面开始钉钉SSO', { corpId: this.$store.state.dingtalkCorpId })
      try {
        const corpId = this.$store.state.dingtalkCorpId || ''
        logSSO('开始获取授权码', { corpId })
        const code = await getAuthCode(corpId)
        logSSO('获取授权码成功', { corpId, codePrefix: code ? code.substring(0, 8) + '...' : 'empty' })
        const resp = await ssoLogin(code)
        logSSO('ssoLogin接口返回', { corpId, code: resp.data.code, message: resp.data.message })
        if (resp.data.code !== 200) {
          this.ssoLoading = false
          logError('ssoLogin失败', { corpId, code: resp.data.code, message: resp.data.message })
          this.errorMsg = resp.data.message || t('login.dingtalkLoginFail')
          return
        }
        const data = resp.data.result
        // 管理员需要手动输入密码
        if (data.needLogin) {
          this.showAdminLogin = true
          this.adminUserName = data.name || ''
          this.adminMobile = data.mobile || ''
          this.ssoToken = data.ssoToken || ''
          this.ssoLoading = false
          logSSO('管理员需要手动输入密码', { name: data.name, mobile: data.mobile })
          return
        }
        const userName = data.userName || data.name || ('user_' + data.mobile)
        this.ssoLoading = false
        this.$store.commit('login', { userName, loginType: 'dingtalk' })
        logSSO('钉钉SSO登录成功', { userName, loginType: 'dingtalk' })
        this.$message({
          message: t('login.loginSuccess'),
          type: 'success',
          center: true,
          duration: 1500
        })
        setTimeout(() => {
          this.$router.push('/vote-rules')
        }, 500)
      } catch (err) {
        this.ssoLoading = false
        logError('钉钉SSO异常', { error: err.message || JSON.stringify(err) })
        this.errorMsg = t('login.dingtalkFail') + ': ' + (err.message || JSON.stringify(err))
      }
    },

    doAdminLogin () {
      this.errorMsg = ''
      if (!this.adminPassword) {
        this.errorMsg = t('login.passwordRequired')
        return
      }
      this.adminLoading = true
      adminLogin(this.adminMobile, this.adminPassword, this.ssoToken).then(resp => {
        this.adminLoading = false
        if (resp.data.code === 200) {
          const userName = resp.data.result || resp.data.data
          this.$store.commit('login', { userName, loginType: 'password' })
          this.$message({
            message: t('login.loginSuccess'),
            type: 'success',
            center: true
          })
          this.$router.push('/vote-rules')
        } else {
          this.errorMsg = resp.data.message || t('login.accountError')
        }
      }).catch(() => {
        this.adminLoading = false
        this.errorMsg = t('login.networkError')
      })
    },

    doLogin () {
      this.errorMsg = ''
      if (!this.userName.trim()) {
        this.errorMsg = t('login.usernameRequired')
        return
      }
      if (!this.password) {
        this.errorMsg = t('login.passwordRequired')
        return
      }
      this.loading = true
      loginByPassword(this.userName.trim(), this.password).then(resp => {
        this.loading = false
        if (resp.data.code === 200) {
          const userName = resp.data.result || resp.data.data
          this.$store.commit('login', { userName, loginType: 'password' })
          this.$message({
            message: t('login.loginSuccess'),
            type: 'success',
            center: true
          })
          this.$router.push('/vote-rules')
        } else {
          this.errorMsg = resp.data.message || t('login.accountError')
        }
      }).catch(() => {
        this.loading = false
        this.errorMsg = t('login.networkError')
      })
    }
  },
  created () {
    document.title = 'IPIP杰出之星投票'
    // 打印当前加载的钉钉配置信息，用于排查测试环境配置是否正确
    const dingtalkConfig = {
      corpId: DINGTALK_CORP_ID,
      corpIdFromStore: this.$store.state.dingtalkCorpId,
      env: process.env.VUE_APP_ENV,
      nodeEnv: process.env.NODE_ENV
    }
    console.log('[Login] 钉钉配置信息:', dingtalkConfig)
    logAccess('Login页面created', { href: window.location.href, inDingTalk: isInDingTalk(), dingtalkConfig })
    const storedUser = window.localStorage.getItem('user')
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser)
        if (user && user.userName) {
          // 已同意规则时直接进入投票页，避免页面重载后重复循环
          const hasAgreed = window.localStorage.getItem('has_agreed_rules')
          logAccess('Login页面已有用户登录', { userName: user.userName, hasAgreed })
          this.$router.push(hasAgreed ? '/vote' : '/vote-rules')
          return
        }
      } catch (e) {
        window.localStorage.removeItem('user')
      }
    }

    // 检测钉钉环境
    if (isInDingTalk()) {
      logAccess('Login页面检测到钉钉环境，开始SSO')
      this.doDingTalkSSO()
    } else {
      logAccess('Login页面未检测到钉钉环境，显示普通登录')
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #e8f0fe 0%, #f0f5ff 50%, #e6f0ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
}
.lang-switch {
  position: absolute;
  top: 16px;
  right: 20px;
  font-size: 14px;
  color: #999;
  z-index: 10;
}
.lang-switch span { cursor: pointer; padding: 2px 4px; transition: color 0.2s; }
.lang-switch span.active { color: #1677ff; font-weight: 600; }
.lang-divider { margin: 0 4px; color: #ddd; cursor: default !important; }
.login-container { width: 100%; max-width: 360px; }
.loading-box { text-align: center; padding: 60px 0; }
.login-header { text-align: center; margin-bottom: 30px; }
.logo-icon { margin-bottom: 16px; }
.login-title {
  font-size: 20px; font-weight: 700; color: #1a1a2e;
  margin: 0 0 8px 0; line-height: 1.4; letter-spacing: 0.5px;
  white-space: pre-line;
}
.login-subtitle { font-size: 14px; color: #999; margin: 0; }
.login-form {
  background: #fff; border-radius: 16px; padding: 28px 24px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
}
.input-group { position: relative; margin-bottom: 16px; }
.input-icon {
  position: absolute; left: 14px; top: 50%;
  transform: translateY(-50%); z-index: 1;
}
.form-input {
  width: 100%; height: 48px; padding: 0 14px 0 44px;
  border: 1px solid #e8e8e8; border-radius: 10px;
  font-size: 16px; color: #333; background: #f7f8fa;
  outline: none; box-sizing: border-box;
  transition: border-color 0.2s, background 0.2s;
  -webkit-appearance: none;
}
.form-input:focus { border-color: #1677ff; background: #fff; }
.form-input::placeholder { color: #bbb; }
.form-input:disabled { background: #eef1f6; color: #666; }
.login-btn {
  width: 100%; height: 48px;
  background: linear-gradient(135deg, #1677ff, #4096ff);
  color: #fff; border: none; border-radius: 10px;
  font-size: 17px; font-weight: 600; letter-spacing: 2px;
  cursor: pointer; margin-top: 8px;
  transition: opacity 0.2s, transform 0.1s;
  -webkit-appearance: none;
}
.login-btn:active { transform: scale(0.98); opacity: 0.9; }
.login-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.error-msg { text-align: center; margin-top: 14px; color: #ff4d4f; font-size: 14px; }
.login-footer { text-align: center; margin-top: 24px; color: #bbb; font-size: 12px; }
</style>


// WEBPACK FOOTER //
// src/login/login.vue